import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
} from '@mui/material';
import { MultipleChoiceQuestion } from './AssessmentQuestionType';
import AssessmentStepControButtonGroup from './AssessmentStepControlButtonGroup';
import {
  AssessmentQuestionAction,
  AssessmentQuestionActionType,
} from './AssessmentQuestionAction';
import APICaller, { MethodType } from '../common/APICaller';
import { useParams } from 'react-router-dom';
import FileDownload from '../common/FileDownload';

interface MultipleChoiceQuestionStepProps {
  data: MultipleChoiceQuestion;
  index: number;
  isLast: boolean;
  isFirst: boolean;
  goNext: () => void;
  goBack: () => void;
  handleSubmit: () => void;
  dispatch: React.Dispatch<AssessmentQuestionAction>;
}

const MultipleChoiceQuestionStep: React.FC<MultipleChoiceQuestionStepProps> = ({
  data,
  index,
  isLast,
  isFirst,
  goNext,
  goBack,
  handleSubmit,
  dispatch,
}) => {
  const initialSelectedOptions: number[] = [];
  Object.keys(data.answer).forEach(choice_id => {
    if (data.answer[+choice_id].chosen) {
      initialSelectedOptions.push(+choice_id);
    }
  });
  const [selectedOptions, setSelectedOptions] = useState<number[]>(
    initialSelectedOptions,
  );
  const { assessmentId } = useParams();

  // Track last saved selections to prevent duplicate saves
  const lastSavedRef = useRef<number[]>(initialSelectedOptions);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const options = data.answer
    ? Object.keys(data.answer)
        .sort((a, b) => +a - +b)
        .map(choice_id => {
          return {
            id: choice_id,
            text: data.answer[+choice_id].text,
          };
        })
    : [];

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optionId = event.target.value;
    const isSelected = selectedOptions.includes(+optionId);

    if (isSelected) {
      setSelectedOptions(selectedOptions.filter(id => id !== +optionId));
    } else {
      setSelectedOptions([...selectedOptions, +optionId]);
    }
  };

  // Autosave function
  const saveAnswer = (selections: number[]) => {
    // Skip if no selections or unchanged
    if (selections.length === 0) {
      return Promise.resolve();
    }

    const selectionsStr = [...selections].sort().join(',');
    const lastSavedStr = [...lastSavedRef.current].sort().join(',');
    
    if (selectionsStr === lastSavedStr) {
      return Promise.resolve();
    }

    const url = data.answerId
      ? `/application/assessments/answers/${data.answerId}/mc/update`
      : `/application/assessments/questions/${data.id}/answer/mc/create`;
    const method = data.answerId ? MethodType.PUT : MethodType.POST;
    const body = {
      answer: {
        possibleChoiceIds: selections,
      },
    };

    return APICaller(url, method, body)
      .then(res => {
        if (!res.errorMessage) {
          if (res.answerId) {
            dispatch({
              type: AssessmentQuestionActionType.UpdateMultipleChoice,
              payload: {
                question_index: index,
                answer: selections,
                answerId: res.answerId,
              },
            });
          } else {
            dispatch({
              type: AssessmentQuestionActionType.UpdateMultipleChoice,
              payload: {
                question_index: index,
                answer: selections,
              },
            });
          }
          lastSavedRef.current = selections;
          // Silent autosave - no notification
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  // Debounced autosave on selection change
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const selectionsStr = [...selectedOptions].sort().join(',');
    const lastSavedStr = [...lastSavedRef.current].sort().join(',');

    if (selectionsStr !== lastSavedStr && selectedOptions.length > 0) {
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswer(selectedOptions);
      }, 800); // 0.8 second delay for MC (faster than text)
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [selectedOptions]);

  const handleNext = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    const selectionsStr = [...selectedOptions].sort().join(',');
    const lastSavedStr = [...lastSavedRef.current].sort().join(',');
    
    if (selectionsStr !== lastSavedStr && selectedOptions.length > 0) {
      await saveAnswer(selectedOptions);
    }
    goNext();
  };

  const handleBack = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    const selectionsStr = [...selectedOptions].sort().join(',');
    const lastSavedStr = [...lastSavedRef.current].sort().join(',');
    
    if (selectionsStr !== lastSavedStr && selectedOptions.length > 0) {
      await saveAnswer(selectedOptions);
    }
    goBack();
  };

  const handleSubmitWithSave = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before submitting if there are unsaved changes
    const selectionsStr = [...selectedOptions].sort().join(',');
    const lastSavedStr = [...lastSavedRef.current].sort().join(',');
    
    if (selectionsStr !== lastSavedStr && selectedOptions.length > 0) {
      await saveAnswer(selectedOptions);
    }
    handleSubmit();
  };

  return (
    <>
      <FormControl component="fieldset">
        <Grid container direction="column" width="100%">
          <Typography variant="h6">{data.description}</Typography>
          {data.hasQuestionFile && (
            <FileDownload
              url={`/assessments/${assessmentId}/question/${data.id}/file/download`}
              displayedText="Download Question"
              width="45%"
            />
          )}
          {options.map(option => (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={selectedOptions.includes(+option.id)}
                  onChange={handleOptionChange}
                  value={option.id}
                />
              }
              label={option.text}
            />
          ))}
        </Grid>
      </FormControl>
      <AssessmentStepControButtonGroup
        isLast={isLast}
        isFirst={isFirst}
        handleNext={handleNext}
        handleBack={handleBack}
        handleSubmit={handleSubmitWithSave}
        showSaveBtn={false}
      />
    </>
  );
};

export default MultipleChoiceQuestionStep;
